import { analytics, performance } from "../../firebase.ts";
import { trace, PerformanceTrace } from "firebase/performance";
import { logEvent, setUserProperties } from "firebase/analytics";

import { Drawer } from "flowbite";
import type { DrawerOptions, DrawerInterface } from "flowbite";
import type { InstanceOptions } from "flowbite";

import { Books } from "../../managers/Books.ts";
import template from "./sw-footer.mjs";
import Strings from "../../models/Strings.json";
import { LANGUAGE } from "../global.ts";
import { SwPage } from "../sw-main/sw-page/sw-page.ts";
import { SwHeader } from "../sw-header/sw-header.ts";
import { SwReader } from "../sw-main/sw-reader/sw-reader.ts";
import { SwMap } from "../sw-main/sw-map/sw-map.ts";
import { SwToc } from "../sw-header/sw-toc/sw-toc.ts";

const strings: any = Strings;

export class SwFooter extends HTMLElement {
    private componentTrace: PerformanceTrace;
    private books = new Books();

    private header = document.querySelector("sw-header") as SwHeader;
    private page = document.querySelector("sw-page") as SwPage;
    private swKhmer = document.querySelector(
        "sw-reader[lang=khmer]"
    ) as SwReader;
    private swPali = document.querySelector("sw-reader[lang=pali]") as SwReader;

    private drawer: DrawerInterface;
    private tabBar: HTMLElement;

    private read: HTMLElement;
    private readership: HTMLElement;
    private share: HTMLElement;
    private learn: HTMLElement;

    private pali: HTMLElement;
    private map: HTMLElement;
    private khmer: HTMLElement;

    private history: HTMLElement;
    private leaderboard: HTMLElement;
    private now: HTMLElement;

    private light: HTMLElement;
    private dark: HTMLElement;
    private auto: HTMLElement;

    // deprecated
    private previous: HTMLElement;
    private next: HTMLElement;
    private bookmark: HTMLElement;

    private audio: HTMLAudioElement; // deprecated

    constructor() {
        super();
        this.componentTrace = trace(performance, "component");
        this.componentTrace.putAttribute("view", "sw-footer");
        this.componentTrace.start();
        this.appendChild(template.content.cloneNode(true));

        this.tabBar = this.querySelector("#sw-footer")!;
        this.pali = this.querySelector("#pali")!;
        this.map = this.querySelector("#map")!;
        this.khmer = this.querySelector("#khmer")!;

        this.read = this.querySelector("#sw-footer-read")!;
        this.readership = this.querySelector("#sw-footer-readership")!;
        this.share = this.querySelector("#sw-footer-share")!;
        this.learn = this.querySelector("#sw-footer-learn")!;

        this.history = this.querySelector("#sw-footer-history")!;
        this.leaderboard = this.querySelector("#sw-footer-leaderboard")!;
        this.now = this.querySelector("#sw-footer-now")!;

        this.light = this.querySelector("#light")!;
        this.dark = this.querySelector("#dark")!;
        this.auto = this.querySelector("#auto")!;

        this.previous = this.querySelector("#previous")!;
        this.next = this.querySelector("#next")!;
        this.bookmark = this.querySelector("#bookmark")!;

        this.audio = this.querySelector("audio")!;

        const options: DrawerOptions = {
            placement: "bottom",
            backdrop: true,
            bodyScrolling: false,
            edge: false,
            edgeOffset: "bottom-[100px]", // doesn't work
            backdropClasses:
                "bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-30",
            onHide: () => {
                this.tabBar.classList.replace("bottom-0", "bottom-[70px]");
            },
            onShow: () => {
                this.tabBar.classList.replace("bottom-[70px]", "bottom-0");
            },
            onToggle: () => {}
        };

        // instance options object
        const instanceOptions: InstanceOptions = {
            id: "sw-footer",
            override: true
        };

        this.drawer = new Drawer(this.tabBar, options, instanceOptions);
    }

    connectedCallback() {
        this.render();

        this.addHomeListeners();
        this.addPageListeners();
        this.addMapListeners();

        this.addPagingListeners();
        this.addColorSchemeListeners();
        this.addNetworkListeners();

        this.componentTrace.incrementMetric("count", 1);
        this.componentTrace.stop();
    }

    show() {
        this.classList.remove("hidden");
    }

    hide() {
        this.classList.add("hidden");
    }

    showHome() {
        this.read.classList.remove("hidden");
        this.readership.classList.remove("hidden");
        this.learn.classList.remove("hidden");
        //this.share.classList.remove("hidden");

        this.pali.classList.add("hidden");
        this.map.classList.add("hidden");
        this.khmer.classList.add("hidden");

        this.history.classList.add("hidden");
        this.leaderboard.classList.add("hidden");
        this.now.classList.add("hidden");
    }

    showPage() {
        this.read.classList.add("hidden");
        this.readership.classList.add("hidden");
        this.learn.classList.add("hidden");
        //this.share.classList.add("hidden");

        this.pali.classList.remove("hidden");
        this.map.classList.remove("hidden");
        this.khmer.classList.remove("hidden");

        this.history.classList.add("hidden");
        this.leaderboard.classList.add("hidden");
        this.now.classList.add("hidden");
    }

    showMap() {
        this.read.classList.add("hidden");
        this.readership.classList.add("hidden");
        this.learn.classList.add("hidden");
        //this.share.classList.add("hidden");

        this.pali.classList.add("hidden");
        this.map.classList.add("hidden");
        this.khmer.classList.add("hidden");

        this.history.classList.remove("hidden");
        this.leaderboard.classList.remove("hidden");
        this.now.classList.remove("hidden");
    }

    private render() {
        if (localStorage.colorScheme === "dark") {
            document.documentElement.classList.add("dark");
        } else if (localStorage.colorScheme === "light") {
            document.documentElement.classList.remove("dark");
        } else {
            if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
                document.documentElement.classList.add("dark");
            } else {
                document.documentElement.classList.remove("dark");
            }
        }
    }

    private addHomeListeners() {
        this.read.addEventListener("click", async () => {
            this.drawer.hide();
            window.location.hash = "#bookmark";
        });

        this.readership.addEventListener("click", () => {
            this.drawer.hide();
            window.location.hash = "#readership";
        });

        this.learn.addEventListener("click", () => {
            const toc = document.querySelector("sw-toc") as SwToc;
            toc.show("pali", 1, 1);
            this.drawer.hide();
        });

        this.share.addEventListener("click", async () => {
            const code = document.querySelector("#qr_code")!
                .parentElement as HTMLPictureElement;
            code.click();
        });
    }

    private addPageListeners() {
        this.pali.addEventListener("click", async () => {
            const paths = window.location.hash.split("/");
            const book = parseInt(paths[2]);
            const page = parseInt(paths[4]);
            await this.books.update(book, "pali");
            this.page.goTo(book, page);

            //const pali = document.getElementById("sw-pali-body") as HTMLElement;
            //navigator.clipboard.writeText(pali.innerText);
            this.swPali.show();
        });

        this.map.addEventListener("click", () => {
            this.drawer.hide();
            window.location.hash = "#readership";
        });

        this.khmer.addEventListener("click", async () => {
            const paths = window.location.hash.split("/");
            const book = parseInt(paths[2]);
            const page = parseInt(paths[4]);
            await this.books.update(book, "khmer");
            this.page.goTo(book, page);

            // const khmer = document.getElementById(
            //     "sw-khmer-body"
            // ) as HTMLElement;
            //navigator.clipboard.writeText(khmer.innerText);
            this.swKhmer.show();
        });
    }

    private addMapListeners() {
        const map = document.querySelector("sw-map") as SwMap;

        this.now.addEventListener("click", () => {
            this.header.showMap("now");
            map.show("now");
            this.drawer.hide();
        });

        this.leaderboard.addEventListener("click", () => {
            this.header.showMap("leaderboard");
            map.show("leaderboard");
            this.drawer.hide();
        });

        this.history.addEventListener("click", () => {
            this.header.showMap("history");
            map.show("history");
            this.drawer.hide();
        });
    }

    //deprecated: safari does not allow
    async checkClipboardPermission(): Promise<boolean> {
        try {
            const result = await navigator.permissions.query({
                name: "clipboard-read" as PermissionName
            });
            return result.state === "granted" || result.state === "prompt";
        } catch (err) {
            console.warn("Clipboard permission check failed: ", err);
            return false;
        }
    }

    // deprecated
    private isAudioPlaying(): boolean {
        return (
            !this.audio.paused &&
            !this.audio.ended &&
            this.audio.currentTime > 0
        );
    }

    // deprecated
    // private addAiListeners() {
    //     this.speak.addEventListener("click", async () => {
    //         //TODO: later use api? or better ai model?
    //         // if (this.isAudioPlaying()) {
    //         //     this.audio.pause();
    //         // } else {
    //         //     const text = await navigator.clipboard.readText();
    //         //     const baseUrl = "https://translate.google.com/translate_tts";
    //         //     const params = new URLSearchParams({
    //         //         ie: "UTF-8",
    //         //         tl: "km",
    //         //         q: text,
    //         //         client: "tw-ob"
    //         //     });
    //         //     this.audio.src = `${baseUrl}?${params.toString()}`;
    //         //     this.audio.play();
    //         // }

    //         let text;
    //         try {
    //             text = await navigator.clipboard.readText();
    //         } catch {
    //             text = "";
    //         }
    //         const encodedText = encodeURIComponent(text);
    //         const translateUrl = `https://translate.google.com/?sl=auto&tl=${strings.language[LANGUAGE]}&text=${encodedText}&op=translate`;
    //         window.open(translateUrl, "_blank");
    //     });

    //     this.tipitaka.addEventListener("click", () => {
    //         //window.open("https://tipitaka.org/khmr/", "_blank");
    //         this.dispatchEvent(
    //             new CustomEvent<{ info: string }>("sw-banner", {
    //                 bubbles: true,
    //                 detail: {
    //                     info: strings.soon[LANGUAGE]
    //                 }
    //             })
    //         );
    //     });

    //     this.buddha.addEventListener("click", async () => {
    //         let text;
    //         try {
    //             text = await navigator.clipboard.readText();
    //         } catch {
    //             text = "";
    //         }
    //         const baseUrl = "https://gemini.google.com/";
    //         const encodedPrompt = encodeURIComponent(text);
    //         const url = `${baseUrl}?model=gpt-4&prompt=${encodedPrompt}`;
    //         window.open(url, "_blank");
    //     });
    // }

    // deprecated
    private addPagingListeners() {
        this.previous.addEventListener("click", () => {
            this.page.pageTo2(false);
            this.drawer.hide();
            //logEvent(analytics, "page", { to: "previous" });
        });
        this.next.addEventListener("click", () => {
            this.page.pageTo2(true);
            this.drawer.hide();
            //logEvent(analytics, "page", { to: "next" });
        });
        this.bookmark.addEventListener("click", (event: Event) => {
            this.header.updateBookmark(true);
            this.drawer.hide();
            //logEvent(analytics, "bookmark", { at: "top" });
        });
    }

    private addColorSchemeListeners() {
        window
            .matchMedia("(prefers-color-scheme: dark)")
            .addEventListener("change", (event: MediaQueryListEvent) => {
                this.render();
                logEvent(analytics, "color", { scheme: "dark" });
            });

        this.light.addEventListener("click", (event: Event) => {
            //localStorage.colorScheme = "light";
            localStorage.setItem("colorScheme", "light");
            this.render();
            this.drawer.hide();
            this.dispatchColorSchemeEvent("light");
            logEvent(analytics, "color", { scheme: "light" });
        });

        this.dark.addEventListener("click", (event: Event) => {
            //localStorage.colorScheme = "dark";
            localStorage.setItem("colorScheme", "dark");
            this.render();
            this.drawer.hide();
            this.dispatchColorSchemeEvent("dark");
            logEvent(analytics, "color", { scheme: "dark" });
        });

        this.auto.addEventListener("click", (event: Event) => {
            //localStorage.colorScheme = "system";
            localStorage.setItem("colorScheme", "system");
            this.render();
            this.drawer.hide();

            this.dispatchEvent(
                new CustomEvent<{ info: string }>("sw-alert", {
                    bubbles: true,
                    detail: {
                        info: strings.automatic[LANGUAGE]
                    }
                })
            );

            this.dispatchColorSchemeEvent("system");
            logEvent(analytics, "color", { scheme: "auto" });
        });
    }

    private addNetworkListeners() {
        if (!navigator.onLine) {
            this.showNetwork(false);
        } else {
            //this.showNetwork(true)
        }
        logEvent(analytics, "network", { status: navigator.onLine });

        window.addEventListener("offline", (event: Event) =>
            this.showNetwork(false)
        );
        window.addEventListener("online", (event: Event) =>
            this.showNetwork(true)
        );
    }

    private showNetwork(isOnline: boolean) {
        this.dispatchEvent(
            new CustomEvent<{ info: string; reload: boolean }>("sw-alert", {
                bubbles: true,
                detail: {
                    info: isOnline
                        ? strings.online[LANGUAGE]
                        : strings.offline[LANGUAGE],
                    reload: false //isOnline
                }
            })
        );
        logEvent(analytics, "network", { status: isOnline });
    }

    private dispatchColorSchemeEvent(colorScheme: string) {
        this.dispatchEvent(
            new CustomEvent<{ colorScheme: string }>("sw-color-scheme", {
                bubbles: true,
                detail: {
                    colorScheme
                }
            })
        );

        logEvent(analytics, "color", { scheme: colorScheme });
    }
}
